import { useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { clampRange } from "./filter-logic";

// A STAND-IN FOR `ngx-slider` IN RANGE MODE, AS `filter-range` CONFIGURES IT (STEP 1, A
// `translate` THAT LABELS THE POINTERS "MIN: $N" / "MAX: $N" AND THE ENDS "$N") AND AS
// `_overrides.scss` RECOLORS IT. GEOMETRY IS NGX-SLIDER'S: A 4PX BAR WITH 35PX ABOVE AND 15PX
// BELOW, 32PX ROUND POINTERS CENTERED ON IT WITH AN 8PX WHITE DOT, POINTER LEFT EDGES SPREAD
// OVER (WIDTH - 32PX), 16PX BUBBLES ABOVE, FLOOR / CEILING BUBBLES HIDDEN WHEN A POINTER BUBBLE
// REACHES THEM, AND ONE COMBINED "MIN - MAX" BUBBLE WHEN THE TWO WOULD OVERLAP. A CLICK ON THE
// BAR MOVES THE NEAREST POINTER; DRAGGING ONE PAST THE OTHER SWITCHES THEM. DISABLED DIMS THE
// WHOLE SLIDER TO 0.3 AND IGNORES INPUT.
const HANDLE = 32;

export function RangeSlider(props: {
  floor: number;
  ceil: number;
  min: number;
  max: number;
  disabled: boolean;
  // THE `formatter` PREFIX ("$" OR "")
  prefix: string;
  minLabel: string;
  maxLabel: string;
  ariaLabel: string;
  onChange: (min: number, max: number) => void;
}) {
  const { floor, ceil, disabled, prefix, minLabel, maxLabel, ariaLabel, onChange } = props;
  const [lo, hi] = clampRange(floor, ceil, props.min, props.max);
  const root = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [active, setActive] = useState<"low" | "high" | null>(null);

  // THE BAR WIDTH, KEPT CURRENT AS THE SIDEBAR RESIZES
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w !== undefined) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const span = Math.max(1, ceil - floor);
  const track = Math.max(0, width - HANDLE);
  const pos = (v: number) => ((v - floor) / span) * track;

  // BUBBLE PLACEMENT NEEDS EACH BUBBLE'S RENDERED WIDTH, SO IT IS APPLIED TO THE DOM AFTER EACH
  // RENDER (NO STATE, SO NO RENDER LOOP)
  const flRef = useRef<HTMLSpanElement>(null);
  const ceRef = useRef<HTMLSpanElement>(null);
  const loRef = useRef<HTMLSpanElement>(null);
  const hiRef = useRef<HTMLSpanElement>(null);
  const cbRef = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const [fl, ce, l, h, cb] = [flRef.current, ceRef.current, loRef.current, hiRef.current, cbRef.current];
    if (!fl || !ce || !l || !h || !cb) return;
    const place = (el: HTMLElement, center: number) => {
      const w = el.offsetWidth;
      const left = Math.min(Math.max(0, center - w / 2), Math.max(0, width - w));
      el.style.left = `${left}px`;
      return { left, right: left + w };
    };
    const lowBox = place(l, pos(lo) + HANDLE / 2);
    const highBox = place(h, pos(hi) + HANDLE / 2);
    const combined = lowBox.right + 10 >= highBox.left;
    const combBox = place(cb, (pos(lo) + pos(hi)) / 2 + HANDLE / 2);
    l.style.visibility = combined ? "hidden" : "visible";
    h.style.visibility = combined ? "hidden" : "visible";
    cb.style.visibility = combined ? "visible" : "hidden";
    fl.style.left = "0px";
    ce.style.left = `${Math.max(0, width - ce.offsetWidth)}px`;
    const leftMost = combined ? combBox.left : lowBox.left;
    const rightMost = combined ? combBox.right : highBox.right;
    fl.style.visibility = leftMost <= fl.offsetWidth + 5 ? "hidden" : "visible";
    ce.style.visibility = rightMost >= width - ce.offsetWidth - 5 ? "hidden" : "visible";
  });

  const valueAt = (clientX: number) => {
    const rect = root.current?.getBoundingClientRect();
    if (!rect || track === 0) return floor;
    const ratio = (clientX - rect.left - HANDLE / 2) / track;
    return Math.min(ceil, Math.max(floor, Math.round(floor + ratio * span)));
  };

  const move = (which: "low" | "high", v: number) => {
    if (which === "low") {
      if (v > hi) {
        onChange(hi, v);
        setActive("high");
      } else onChange(v, hi);
    } else if (v < lo) {
      onChange(v, lo);
      setActive("low");
    } else onChange(lo, v);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (disabled || e.button !== 0) return;
    const v = valueAt(e.clientX);
    const target = (e.target as HTMLElement).dataset["handle"] as "low" | "high" | undefined;
    const which = target ?? (Math.abs(v - lo) <= Math.abs(v - hi) && !(v > hi) ? "low" : "high");
    setActive(which);
    e.currentTarget.setPointerCapture(e.pointerId);
    if (!target) move(which, v);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!active || disabled) return;
    move(active, valueAt(e.clientX));
  };
  const endDrag = () => setActive(null);

  const onKey = (which: "low" | "high") => (e: KeyboardEvent<HTMLSpanElement>) => {
    if (disabled) return;
    const current = which === "low" ? lo : hi;
    const page = Math.max(1, Math.round(span / 10));
    const step: Record<string, number> = { ArrowLeft: -1, ArrowDown: -1, ArrowRight: 1, ArrowUp: 1, PageDown: -page, PageUp: page };
    let v: number | undefined;
    if (e.key in step) v = current + (step[e.key] ?? 0);
    else if (e.key === "Home") v = floor;
    else if (e.key === "End") v = ceil;
    if (v === undefined) return;
    e.preventDefault();
    move(which, Math.min(ceil, Math.max(floor, v)));
  };

  const text = (label: string, v: number): ReactNode => (
    <>
      <b>{label}:</b> {prefix}
      {v}
    </>
  );

  return (
    <div
      ref={root}
      className={`pillars-range${disabled ? " pillars-range-disabled" : ""}`}
      aria-disabled={disabled}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <span className="pillars-range-bar" />
      <span className="pillars-range-selection" style={{ left: pos(lo) + HANDLE / 2, width: Math.max(0, pos(hi) - pos(lo)) }} />
      {(["low", "high"] as const).map((which) => {
        const v = which === "low" ? lo : hi;
        return (
          <span
            key={which}
            data-handle={which}
            role="slider"
            tabIndex={disabled ? -1 : 0}
            aria-label={`${ariaLabel} ${which === "low" ? minLabel : maxLabel}`}
            aria-valuemin={floor}
            aria-valuemax={ceil}
            aria-valuenow={v}
            aria-disabled={disabled}
            className={`pillars-range-pointer${active === which ? " pillars-range-active" : ""}`}
            style={{ left: pos(v), zIndex: active === which ? 4 : 3 }}
            onKeyDown={onKey(which)}
          />
        );
      })}
      <span ref={flRef} className="pillars-range-bubble">
        {prefix}
        {floor}
      </span>
      <span ref={ceRef} className="pillars-range-bubble">
        {prefix}
        {ceil}
      </span>
      <span ref={loRef} className="pillars-range-bubble">
        {text(minLabel, lo)}
      </span>
      <span ref={hiRef} className="pillars-range-bubble">
        {text(maxLabel, hi)}
      </span>
      <span ref={cbRef} className="pillars-range-bubble">
        {text(minLabel, lo)} - {text(maxLabel, hi)}
      </span>
    </div>
  );
}
